import React, { useRef, useEffect, useCallback } from 'react';

import { useMap } from 'react-map-gl';

interface PinProps {
    size?: number;
}

function Pin({ size = 48 }: PinProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const requestIdRef = useRef<number | null>(null);

    const { current: map } = useMap();

    const renderFrame = useCallback(() => {
        const duration = 1250;
        const t = (performance.now() % duration) / duration;

        const width = size;
        const height = size;

        const radius = (size / 2) * 0.3;
        const outerRadius = (size / 2) * 0.7 * t + radius;

        const context = canvasRef?.current?.getContext('2d');

        if (!context) return;

        // Draw the outer circle.
        context.clearRect(0, 0, width, height);
        context.beginPath();
        context.arc(width / 2, height / 2, outerRadius, 0, Math.PI * 2);
        context.fillStyle = `rgba(147, 141, 249,${1 - t})`;
        context.fill();

        // Draw the inner circle.
        context.beginPath();
        context.arc(width / 2, height / 2, radius, 0, Math.PI * 2);
        context.fillStyle = '#938DF9';
        context.strokeStyle = 'transparent';
        context.lineWidth = 2 + 4 * (1 - t);
        context.fill();
        context.stroke();

        // Update image's data with data from the canvas.
        // Continuously repaint the map, resulting
        // in the smooth animation of the dot.
        map?.triggerRepaint();
    }, [map, size]);

    const tick = useCallback(() => {
        if (!canvasRef.current) return;
        renderFrame();
        requestIdRef.current = requestAnimationFrame(tick);
    }, [renderFrame]);

    useEffect(() => {
        requestIdRef.current = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(requestIdRef.current as number);
        };
    }, [tick]);

    return <canvas width={size} height={size} ref={canvasRef} />;
}

export default React.memo(Pin);
